import React, {ChangeEvent, FormEvent, useEffect, useState} from 'react';
import {SENSITIVE_DATA_PLACEHOLDER} from "../../AppConst";
import {MerchantResponse, updateMerchant} from "../../api/MerchantsApi";

type EditMerchantAccountProps = {
    showEditPopup: boolean;
    selectedMerchant: MerchantResponse | null;
    handleCloseEdit: () => void;
    fetchMerchants: () => void;
    setSelectedMerchant: (merchant: MerchantResponse | null) => void;
};

type FormData = {
    apiKey: string;
    clientKey: string;
    reportApiKey: string;
    adyenPrefix: string;
};

type EditMode = {
    apiKey: boolean;
    clientKey: boolean;
    reportApiKey: boolean;
    adyenPrefix: boolean;
};

const EditMerchantAccountComponent: React.FC<EditMerchantAccountProps> = ({
                                                                     showEditPopup,
                                                                     selectedMerchant,
                                                                     handleCloseEdit,
                                                                     fetchMerchants,
                                                                     setSelectedMerchant,
                                                                 }) => {
    const [formData, setFormData] = useState<FormData>({
        apiKey: '',
        clientKey: '',
        reportApiKey: '',
        adyenPrefix: '',
    });

    const [editMode, setEditMode] = useState<EditMode>({
        apiKey: false,
        clientKey: false,
        reportApiKey: false,
        adyenPrefix: false,
    });

    useEffect(() => {
        if (showEditPopup && selectedMerchant) {
            const initialState: FormData = {
                apiKey: SENSITIVE_DATA_PLACEHOLDER,
                clientKey: selectedMerchant.clientKey
                    ? SENSITIVE_DATA_PLACEHOLDER
                    : '',
                reportApiKey: selectedMerchant.reportApiKey
                    ? SENSITIVE_DATA_PLACEHOLDER
                    : '',
                adyenPrefix: selectedMerchant.adyenPrefix
                ,
            };
            setFormData(initialState);
            setEditMode({
                apiKey: false,
                clientKey: false,
                reportApiKey: false,
                adyenPrefix: false,
            });
        }
    }, [showEditPopup, selectedMerchant]);

    const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setFormData((prevData) => ({...prevData, [name]: value}));
    };

    const toggleEditMode = (fieldName: keyof EditMode) => {
        setEditMode((prevMode) => ({...prevMode, [fieldName]: true}));
        if (formData[fieldName] === SENSITIVE_DATA_PLACEHOLDER) {
            setFormData((prevData) => ({...prevData, [fieldName]: ''}));
        }
    };

    const handleCancel = (fieldName: keyof FormData) => {
        setFormData((prevData) => ({
            ...prevData,
            [fieldName]: SENSITIVE_DATA_PLACEHOLDER,
        }));
        setEditMode((prevMode) => ({...prevMode, [fieldName]: false}));
    };

    const handleClose = () => {
        handleCloseEdit();
    };

    const updateMerchantAccount = async () => {
        const updatedData = Object.keys(formData).reduce(
            (acc: FormData, key) => {
                const field = key as keyof FormData;
                if (formData[field] !== SENSITIVE_DATA_PLACEHOLDER) {
                    acc[field] = formData[field];
                }
                return acc;
            },
            {} as FormData
        );

        if (
            !Object.keys(updatedData).length ||
            Object.values(updatedData).every(
                (value) => value === SENSITIVE_DATA_PLACEHOLDER
            )
        ) {
            handleClose();
            return;
        }

        try {
            await updateMerchant(selectedMerchant?.merchantId as string, updatedData);
            fetchMerchants();
            handleClose();
        } catch (error) {
            alert(
                'Failed to update merchant. Please check your connection and try again.'
            );
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        await updateMerchantAccount();
        setSelectedMerchant(null);
    };

    if (!showEditPopup || !selectedMerchant) return <></>;

    return (
        <div id="edit-popup" className="popup">
            <div className="popup-content">
                <h2>Edit Merchant Account</h2>
                <button className="close-btn" onClick={handleClose}>
                    X
                </button>
                <form onSubmit={handleSubmit}>
                    <table className="form-table">
                        <tbody>
                        <tr id="selected-merchant-name">
                            <td>
                                <label className="merchant-name">
                                    Merchant Account
                                </label>
                            </td>
                            <td>
                                <p className="merchant-name">
                                    {selectedMerchant?.merchantId}
                                </p>
                            </td>
                            <td></td>
                        </tr>
                        {(
                            [
                                'apiKey',
                                'clientKey',
                                'reportApiKey',
                                'adyenPrefix',
                            ] as Array<keyof FormData>
                        ).map((fieldName) => (
                            <tr key={fieldName}>
                                <td>
                                    <label>
                                        {fieldName === 'apiKey' &&
                                            'Merchant API Key'}
                                        {fieldName === 'clientKey' &&
                                            'Client Key'}
                                        {fieldName === 'reportApiKey' &&
                                            'Report API Key'}
                                        {fieldName === 'adyenPrefix' &&
                                            'Live endpoint prefix'}
                                    </label>
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        name={fieldName}
                                        value={formData[fieldName]}
                                        onChange={handleFieldChange}
                                        disabled={!editMode[fieldName]}
                                    />
                                </td>
                                <td>
                                    <div className="button-group">
                                        {!editMode[fieldName] && (
                                            <button
                                                type="button"
                                                className="btn btn-primary-light"
                                                onClick={() =>
                                                    toggleEditMode(
                                                        fieldName
                                                    )
                                                }
                                            >
                                                Edit
                                            </button>
                                        )}
                                        {editMode[fieldName] && (
                                            <button
                                                type="button"
                                                className="btn btn-primary-light"
                                                onClick={() =>
                                                    handleCancel(
                                                        fieldName
                                                    )
                                                }
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <div className="form-actions">
                        <button type="submit" className="btn btn-primary">
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditMerchantAccountComponent;